'use client';
/**
 * 「{지역} 인기 단지 · 인기 검색어」 클라이언트 훅 — 수화 뒤에만(서버 HTML 은 방문자 무관 · 지역 기억은 브라우저에만 있다).
 * 계약 = s-pop GET /api/search/popular?sido=&sigungu=[&limit][&exclude] (lib/search/pop-region toPopularRegionData).
 * 플래그 ui.popular_region(NavData.popularOn · 기본 off)이 꺼져 있으면 요청하지 않는다(null).
 * 같은 질의는 탭 안에서 한 번만 받는다(모듈 캐시). 실패·빈 값 = EMPTY(블록 생략).
 */
import { useEffect, useState } from 'react';
import { useNavData } from '@/components/nav/nav-data-context';
import { EMPTY_POPULAR, popularQuery, toPopularRegionData, type PopularRegionData } from './pop-region';

const cache = new Map<string, Promise<PopularRegionData>>();
const tabCache = new Map<string, Promise<string[]>>();

export interface PopularFetchOpts { limit?: number; exclude?: string | null }

export function fetchPopularRegion(sido: string | null, sigungu: string | null, opts: PopularFetchOpts = {}): Promise<PopularRegionData> {
  const qs = popularQuery(sido, sigungu, opts);
  const label = sigungu && sido ? `${sido} ${sigungu}` : sido ?? '전국';
  let p = cache.get(qs);
  if (!p) {
    p = fetch(`/api/search/popular${qs ? `?${qs}` : ''}`)
      .then(async (r) => (r.ok ? toPopularRegionData(await r.json(), label, opts.limit ?? 10) : { ...EMPTY_POPULAR, label }))
      .catch(() => { cache.delete(qs); return { ...EMPTY_POPULAR, label }; });
    cache.set(qs, p);
  }
  return p;
}

export function fetchPopularTabs(sido: string): Promise<string[]> {
  let p = tabCache.get(sido);
  if (!p) {
    p = fetch(`/api/search/popular-tabs?sido=${encodeURIComponent(sido)}`)
      .then(async (r) => (r.ok ? ((await r.json())?.tabs ?? []) : []))
      .then((t: unknown) => (Array.isArray(t) ? t.filter((x): x is string => typeof x === 'string') : []))
      .catch(() => { tabCache.delete(sido); return []; });
    tabCache.set(sido, p);
  }
  return p;
}

/** enabled=false 이거나 플래그 off 면 요청하지 않는다(null). */
export function usePopularRegion(sido: string | null, sigungu: string | null, enabled = true, opts: PopularFetchOpts = {}): PopularRegionData | null {
  const { popularOn } = useNavData();
  const on = enabled && popularOn;
  const [data, setData] = useState<PopularRegionData | null>(null);
  const { limit, exclude = null } = opts;
  useEffect(() => {
    if (!on) { setData(null); return; }
    let alive = true;
    void fetchPopularRegion(sido, sigungu, { limit, exclude }).then((d) => { if (alive) setData(d); });
    return () => { alive = false; };
  }, [sido, sigungu, on, limit, exclude]);
  return data;
}

export function usePopularTabs(sido: string | null, enabled = true): string[] {
  const { popularOn } = useNavData();
  const [tabs, setTabs] = useState<string[]>([]);
  useEffect(() => {
    if (!enabled || !popularOn || !sido) { setTabs([]); return; }
    let alive = true;
    void fetchPopularTabs(sido).then((t) => { if (alive) setTabs(t); });
    return () => { alive = false; };
  }, [sido, enabled, popularOn]);
  return tabs;
}
